import { R, D, ROW_H, EVEN_COLS, ODD_COLS } from './types';

export function key(row: number, col: number): string {
  return `${row},${col}`;
}

// Effective parity drives a row's width, X-offset and neighbour rules.
// Tying it to (row + dropCount) keeps every existing bubble's parity stable
// when the ceiling drops (we add 1 to both row and dropCount).
export function effParity(row: number, dropCount: number): 0 | 1 {
  return (((row + dropCount) % 2) + 2) % 2 === 0 ? 0 : 1;
}

export function colsInRow(row: number, dropCount: number): number {
  return effParity(row, dropCount) === 0 ? EVEN_COLS : ODD_COLS;
}

export function cellCenterX(col: number, parity: 0 | 1): number {
  // even rows: 20,60,…,420 ; odd rows shifted right by R: 40,80,…,420
  return parity === 0 ? R + col * D : D + col * D;
}

export function cellCenterY(row: number): number {
  return R + row * ROW_H;
}

export function cellCenter(row: number, col: number, dropCount: number): { x: number; y: number } {
  return { x: cellCenterX(col, effParity(row, dropCount)), y: cellCenterY(row) };
}

export function isValidCell(row: number, col: number, dropCount: number): boolean {
  return row >= 0 && col >= 0 && col < colsInRow(row, dropCount);
}

// The 6 hex neighbours of a cell (may include out-of-range cols; filter as needed).
export function neighbors(row: number, col: number, dropCount: number): [number, number][] {
  const p = effParity(row, dropCount);
  if (p === 0) {
    return [
      [row, col - 1], [row, col + 1],
      [row - 1, col - 1], [row - 1, col],
      [row + 1, col - 1], [row + 1, col],
    ];
  }
  return [
    [row, col - 1], [row, col + 1],
    [row - 1, col], [row - 1, col + 1],
    [row + 1, col], [row + 1, col + 1],
  ];
}

// Nearest grid cell to a pixel point (clamped into valid range for that row).
export function pixelToCell(x: number, y: number, dropCount: number): { row: number; col: number } {
  let row = Math.round((y - R) / ROW_H);
  if (row < 0) row = 0;
  const p = effParity(row, dropCount);
  const cols = p === 0 ? EVEN_COLS : ODD_COLS;
  const col0 = p === 0 ? (x - R) / D : (x - D) / D;
  let col = Math.round(col0);
  if (col < 0) col = 0;
  if (col > cols - 1) col = cols - 1;
  return { row, col };
}

// Snap a stopped projectile to the nearest empty, valid cell. Candidates = the
// base cell plus its neighbours (the projectile always halts touching the grid
// or ceiling, so the target empty cell is one of these).
export function snapCell(
  x: number, y: number, dropCount: number, occupied: Set<string>,
): { row: number; col: number } {
  const base = pixelToCell(x, y, dropCount);
  const candidates: [number, number][] = [[base.row, base.col], ...neighbors(base.row, base.col, dropCount)];

  let best: { row: number; col: number } | null = null;
  let bestDist = Infinity;
  for (const [r, c] of candidates) {
    if (!isValidCell(r, c, dropCount)) continue;
    if (occupied.has(key(r, c))) continue;
    const ctr = cellCenter(r, c, dropCount);
    const dist = (ctr.x - x) ** 2 + (ctr.y - y) ** 2;
    if (dist < bestDist) { bestDist = dist; best = { row: r, col: c }; }
  }
  // Fallback: scan a small window if no candidate was free (rare).
  if (!best) {
    for (let r = Math.max(0, base.row - 1); r <= base.row + 1; r++) {
      const cols = colsInRow(r, dropCount);
      for (let c = 0; c < cols; c++) {
        if (occupied.has(key(r, c))) continue;
        const ctr = cellCenter(r, c, dropCount);
        const dist = (ctr.x - x) ** 2 + (ctr.y - y) ** 2;
        if (dist < bestDist) { bestDist = dist; best = { row: r, col: c }; }
      }
    }
  }
  return best ?? base;
}
